import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/firebase';
import WIcon from '../components/shared/WIcon';
import Modal from '../components/shared/Modal';

const STATUS_LABEL = {
  pending: 'Pending',
  reviewed: 'Reviewed',
  approved: 'Approved',
  rejected: 'Rejected',
};

const STATUS_CLASS = {
  pending: 'paused',
  reviewed: 'basic',
  approved: 'active',
  rejected: 'inactive',
};

const TYPE_PILL = {
  company: { label: 'Company', color: '#00bf63', bg: 'rgba(0,191,99,0.10)' },
  vendor:  { label: 'Vendor',  color: '#8b5cf6', bg: 'rgba(139,92,246,0.10)' },
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const initialsFor = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();

const ApplicationDetailModal = ({ application, onClose, onChanged }) => {
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState(application.notes || '');
  const typeMeta = TYPE_PILL[application.type] || TYPE_PILL.company;

  const updateStatus = async (status) => {
    setSubmitting(true);
    try {
      await updateDoc(doc(db, 'applications', application.id), {
        status,
        notes,
        reviewedAt: new Date(),
        reviewedBy: auth.currentUser?.email || null,
      });
      onChanged?.();
      onClose?.();
    } catch (err) {
      console.error('Failed to update application:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const saveNotes = async () => {
    setSubmitting(true);
    try {
      await updateDoc(doc(db, 'applications', application.id), { notes });
      onChanged?.();
      onClose?.();
    } catch (err) {
      console.error('Failed to save notes:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={null} onClose={onClose} width={560}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: typeMeta.bg,
            color: typeMeta.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 800, flexShrink: 0,
          }}>{initialsFor(application.companyName)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {application.companyName}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <span style={{
                padding: '2px 9px', borderRadius: 999,
                background: typeMeta.bg, color: typeMeta.color,
                fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
              }}>{typeMeta.label}</span>
              <span className={`loysta-status ${STATUS_CLASS[application.status] || 'paused'}`}>
                {STATUS_LABEL[application.status] || 'Pending'}
              </span>
            </div>
          </div>
        </div>

        <Section label="Description">
          <p style={{ fontSize: 14, color: '#334155', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
            {application.description || '—'}
          </p>
        </Section>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Section label="Contact person">
            <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
              {application.contactPerson || '—'}
            </div>
          </Section>
          <Section label="Submitted">
            <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
              {formatDate(application.createdAt)}
            </div>
          </Section>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Section label="Email">
            <a
              href={`mailto:${application.email}`}
              style={{ fontSize: 14, fontWeight: 600, color: '#00bf63', textDecoration: 'none' }}
            >{application.email}</a>
          </Section>
          <Section label="Phone">
            <a
              href={`tel:${application.phone}`}
              style={{ fontSize: 14, fontWeight: 600, color: '#00bf63', textDecoration: 'none' }}
            >{application.phone}</a>
          </Section>
        </div>

        <Section label="Internal notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes from your team — only visible to super admins."
            rows={3}
            style={{
              width: '100%',
              border: '1.5px solid #e2e8f0',
              borderRadius: 10,
              padding: '10px 12px',
              fontSize: 13,
              fontFamily: 'inherit',
              color: '#0f172a',
              background: '#f8fafc',
              outline: 'none',
              resize: 'vertical',
              minHeight: 70,
            }}
          />
        </Section>

        {application.reviewedAt && (
          <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.5 }}>
            Last reviewed {formatDate(application.reviewedAt)}
            {application.reviewedBy ? ` by ${application.reviewedBy}` : ''}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
          <button type="button" className="cancel-btn" onClick={onClose} disabled={submitting}>Close</button>
          <button
            type="button"
            onClick={saveNotes}
            disabled={submitting}
            style={{
              padding: '9px 18px', border: '1.5px solid #e2e8f0',
              borderRadius: 8, background: 'white', color: '#334155',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
            }}
          >Save notes</button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={() => updateStatus('rejected')}
            disabled={submitting}
            style={{
              padding: '9px 16px', border: 'none', borderRadius: 8,
              background: 'rgba(239,68,68,0.10)', color: '#dc2626',
              fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
            }}
          >Reject</button>
          <button
            type="button"
            onClick={() => updateStatus('reviewed')}
            disabled={submitting}
            style={{
              padding: '9px 16px', border: 'none', borderRadius: 8,
              background: '#0f172a', color: 'white',
              fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
            }}
          >Mark reviewed</button>
          <button
            type="button"
            onClick={() => updateStatus('approved')}
            disabled={submitting}
            style={{
              padding: '9px 16px', border: 'none', borderRadius: 8,
              background: '#00bf63', color: 'white',
              fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
            }}
          >Approve</button>
        </div>
      </div>
    </Modal>
  );
};

const Section = ({ label, children }) => (
  <div>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
      {label}
    </div>
    {children}
  </div>
);

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [open, setOpen] = useState(null);

  const fetchApplications = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'applications'));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const da = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const dbb = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return dbb - da;
      });
      setApplications(list);
    } catch (err) {
      console.error('Failed to load applications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchApplications(); }, [fetchApplications]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this application? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'applications', id));
      fetchApplications();
    } catch (err) {
      console.error('Failed to delete application:', err);
    }
  };

  const counts = useMemo(() => {
    const total = applications.length;
    const pending = applications.filter(a => (a.status || 'pending') === 'pending').length;
    const company = applications.filter(a => a.type === 'company').length;
    const vendor = applications.filter(a => a.type === 'vendor').length;
    return { total, pending, company, vendor };
  }, [applications]);

  const filtered = useMemo(() => {
    let list = applications;
    if (typeFilter !== 'all') list = list.filter(a => a.type === typeFilter);
    if (statusFilter !== 'all') list = list.filter(a => (a.status || 'pending') === statusFilter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(a =>
        (a.companyName || '').toLowerCase().includes(s) ||
        (a.contactPerson || '').toLowerCase().includes(s) ||
        (a.email || '').toLowerCase().includes(s) ||
        (a.phone || '').toLowerCase().includes(s),
      );
    }
    return list;
  }, [applications, typeFilter, statusFilter, search]);

  const typeFilters = [
    { id: 'all', label: 'All', count: counts.total },
    { id: 'company', label: 'Company', count: counts.company },
    { id: 'vendor', label: 'Vendor', count: counts.vendor },
  ];

  const statusFilters = [
    { id: 'all', label: 'Any status' },
    { id: 'pending', label: 'Pending' },
    { id: 'reviewed', label: 'Reviewed' },
    { id: 'approved', label: 'Approved' },
    { id: 'rejected', label: 'Rejected' },
  ];

  return (
    <>
      {open && (
        <ApplicationDetailModal
          application={open}
          onClose={() => setOpen(null)}
          onChanged={fetchApplications}
        />
      )}

      {/* Summary cards */}
      <div className="loysta-stat-grid" style={{ marginBottom: 24 }}>
        <SummaryCard label="Total applications" value={counts.total} icon="briefcase" color="#3b82f6" />
        <SummaryCard label="Pending review" value={counts.pending} icon="bell" color="#f59e0b" highlight />
        <SummaryCard label="Companies" value={counts.company} icon="building" color="#00bf63" />
        <SummaryCard label="Vendors" value={counts.vendor} icon="store" color="#8b5cf6" />
      </div>

      <div className="loysta-card">
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220, display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 7, padding: '7px 12px' }}>
            <WIcon name="search" size={15} color="#94a3b8" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search applications…"
              style={{ border: 'none', background: 'none', fontSize: 13, fontFamily: 'inherit', color: '#0f172a', outline: 'none', width: '100%' }}
            />
          </div>

          {typeFilters.map((t) => {
            const active = typeFilter === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTypeFilter(t.id)}
                style={{
                  padding: '7px 14px',
                  borderRadius: 7,
                  border: `1.5px solid ${active ? '#00bf63' : '#e2e8f0'}`,
                  background: active ? '#00bf63' : 'white',
                  color: active ? 'white' : '#334155',
                  fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
                }}
              >
                {t.label} <span style={{ marginLeft: 4, opacity: 0.7 }}>{t.count}</span>
              </button>
            );
          })}

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '7px 12px', borderRadius: 7,
              border: '1.5px solid #e2e8f0', background: 'white',
              color: '#334155', fontSize: 12, fontWeight: 600,
              fontFamily: 'inherit', cursor: 'pointer', outline: 'none',
            }}
          >
            {statusFilters.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>

        <table className="loysta-table">
          <thead>
            <tr>
              <th>Applicant</th>
              <th>Type</th>
              <th>Contact</th>
              <th>Submitted</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading applications…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                {applications.length === 0
                  ? 'No applications yet. Once someone applies on the marketing site, they\'ll appear here.'
                  : 'No applications match your filters.'}
              </td></tr>
            ) : filtered.map((a) => {
              const typeMeta = TYPE_PILL[a.type] || TYPE_PILL.company;
              const status = a.status || 'pending';
              return (
                <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => setOpen(a)}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: 8,
                        background: typeMeta.bg, color: typeMeta.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, flexShrink: 0,
                      }}>{initialsFor(a.companyName)}</div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{a.companyName || 'Unnamed'}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.description || ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span style={{
                      padding: '3px 10px', borderRadius: 999,
                      background: typeMeta.bg, color: typeMeta.color,
                      fontSize: 12, fontWeight: 600,
                    }}>{typeMeta.label}</span>
                  </td>
                  <td>
                    <div style={{ fontSize: 13, color: '#334155' }}>{a.contactPerson || '—'}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{a.email || '—'}</div>
                  </td>
                  <td style={{ color: '#94a3b8', fontSize: 12 }}>{formatDate(a.createdAt)}</td>
                  <td><span className={`loysta-status ${STATUS_CLASS[status] || 'paused'}`}>{STATUS_LABEL[status] || 'Pending'}</span></td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 2 }}>
                      <button className="loysta-icon-btn" onClick={() => setOpen(a)} title="Open">
                        <WIcon name="eye" size={15} />
                      </button>
                      <button className="loysta-icon-btn danger" onClick={() => handleDelete(a.id)} title="Delete">
                        <WIcon name="trash" size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

const SummaryCard = ({ label, value, icon, color, highlight }) => (
  <div className="loysta-stat-card" style={highlight ? { borderColor: color + '40' } : undefined}>
    <div className="loysta-stat-card-head">
      <span className="loysta-stat-card-label">{label}</span>
      <div className="loysta-stat-card-icon" style={{ background: color + '18', color }}>
        <WIcon name={icon} size={18} />
      </div>
    </div>
    <div className="loysta-stat-card-value">{value}</div>
  </div>
);

export default Applications;
